import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '../api';
import { Query, QueryState, Result } from '../interfaces/queryBuilderInterfaces';

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const newQuery = createAsyncThunk<Query, FormData>('dashboard/new', async (formData) => {
    const response = await axiosInstance.post(`/rest/olapquery/new`, formData, {
        headers: {'Content-Type': 'multipart/form-data'},
    });
    return response.data;
});

export const setupQuery = createAsyncThunk<Query, Query>('dashboard/setup', async (payload) => {
    const response = await axiosInstance.post(`/rest/olapquery/${payload.name}/setup`, payload);
    return response.data;
});

export const vioAsofDataResultQuery = createAsyncThunk<Result, Query>('dashboard/vioAsof/result', async (payload) => {
    let response = await axiosInstance.get(`/rest/olapquery/${payload.name}/result/flattened`);
    if(response.data.result.resultFill === 'TimedOut') { 
        do {
            await delay(1000); 
            response = await axiosInstance.get(`/rest/olapquery/result/checkstatus/${response.data.uid}`);
        } while (response.data.result.resultFill === 'TimedOut');
        
    }
    return response.data;
});

export const cardResultQuery = createAsyncThunk<Result, Query>('dashboard/card/result', async (payload) => {
    let response = await axiosInstance.get(`/rest/olapquery/${payload.name}/result/flattened`);
    if(response.data.result.resultFill === 'TimedOut') { 
        do {
            await delay(1000); 
            response = await axiosInstance.get(`/rest/olapquery/result/checkstatus/${response.data.uid}`);
        } while (response.data.result.resultFill === 'TimedOut');
        
    }
    return response.data;
});

export const vioResultQuery = createAsyncThunk<Result, Query>('dashboard/vio/result', async (payload) => {
    let response = await axiosInstance.get(`/rest/olapquery/${payload.name}/result/flattened`);
    if(response.data.result.resultFill === 'TimedOut') { 
        do {
            await delay(1000); 
            response = await axiosInstance.get(`/rest/olapquery/result/checkstatus/${response.data.uid}`);
        } while (response.data.result.resultFill === 'TimedOut');
        
    }
    return response.data;
});

export const lineCytdResultQuery = createAsyncThunk<Result, Query>('dashboard/line/cytd/result', async (payload) => {
    let response = await axiosInstance.get(`/rest/olapquery/${payload.name}/result/flattened`);
    if(response.data.result.resultFill === 'TimedOut') { 
        do {
            await delay(1000); 
            response = await axiosInstance.get(`/rest/olapquery/result/checkstatus/${response.data.uid}`);
        } while (response.data.result.resultFill === 'TimedOut');
        
    }
    return response.data;
});

export const linePytdResultQuery = createAsyncThunk<Result, Query>('dashboard/line/pytd/result', async (payload) => {
    let response = await axiosInstance.get(`/rest/olapquery/${payload.name}/result/flattened`);
    if(response.data.result.resultFill === 'TimedOut') { 
        do {
            await delay(1000); 
            response = await axiosInstance.get(`/rest/olapquery/result/checkstatus/${response.data.uid}`);
        } while (response.data.result.resultFill === 'TimedOut');
        
    }
    return response.data;
});

const initialState: any = {
    dashboardQuery: null,
    results: null,
    loading: true,
    error: null,
    vioDateResults: null,
    cardResults: null,
    vioResults: null,
    cytdLineResults: null,
    pytdLineResults: null,
};

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        setQuery(state, action: PayloadAction<any>) {
            state.query = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(newQuery.pending, (state) => {
                        state.loading = true;
                    })
                    .addCase(newQuery.fulfilled, (state, action) => {
                        state.dashboardQuery = action.payload;
                    })
                    .addCase(newQuery.rejected, (state, action) => {
                        state.loading = false;
                        state.error = action.error.message || null; 
                    })
                    .addCase(setupQuery.pending, (state) => {
                        state.loading = true;
                    })
                    .addCase(setupQuery.fulfilled, (state, action) => {
                        state.dashboardQuery = action.payload;
                    })
                    .addCase(setupQuery.rejected, (state, action) => {
                        state.loading = false;
                        state.error = action.error.message || null; 
                    })
                    .addCase(vioAsofDataResultQuery.pending, (state) => {
                        state.loading = true;
                    })
                    .addCase(vioAsofDataResultQuery.fulfilled, (state, action) => {
                        state.loading = false;
                        state.vioDateResults = action.payload;
                    })
                    .addCase(vioAsofDataResultQuery.rejected, (state, action) => {
                        state.loading = false;
                        state.error = action.error.message || null; 
                    })
                    .addCase(cardResultQuery.pending, (state) => {
                        state.loading = true;
                    })
                    .addCase(cardResultQuery.fulfilled, (state, action) => {
                        state.loading = false;
                        state.cardResults = action.payload;
                    })
                    .addCase(cardResultQuery.rejected, (state, action) => {
                        state.loading = false;
                        state.error = action.error.message || null; 
                    })
                    .addCase(vioResultQuery.pending, (state) => {
                        state.loading = true;
                    })
                    .addCase(vioResultQuery.fulfilled, (state, action) => {
                        state.loading = false;
                        state.vioResults = action.payload;
                    })
                    .addCase(vioResultQuery.rejected, (state, action) => {
                        state.loading = false;
                        state.error = action.error.message || null; 
                    })
                    .addCase(lineCytdResultQuery.pending, (state) => {
                        state.loading = true;
                    })
                    .addCase(lineCytdResultQuery.fulfilled, (state, action) => {
                        state.loading = false;
                        state.cytdLineResults = action.payload;
                    })
                    .addCase(lineCytdResultQuery.rejected, (state, action) => {
                        state.loading = false;
                        state.error = action.error.message || null; 
                    })
                    .addCase(linePytdResultQuery.pending, (state) => {
                        state.loading = true;
                    })
                    .addCase(linePytdResultQuery.fulfilled, (state, action) => {
                        state.loading = false;
                        state.pytdLineResults = action.payload;
                    })
                    .addCase(linePytdResultQuery.rejected, (state, action) => {
                        state.loading = false;
                        state.error = action.error.message || null; 
                    });
    },
});

export const { setQuery } = dashboardSlice.actions;
export default dashboardSlice.reducer;
